import { ModelBase } from "@shoothill/core";
import { action, computed, observable } from "mobx";
import { RFIFormDocumentModel, RFIDocumentDTO } from "../Common/RFIFormDocumentModel";
import { RFIFormFileModel, RFIFileDTO } from "../Common/RFIFormFileModel";

export class RFICreateFormModel extends ModelBase<RFICreateFormModel> {
    public static readonly DEFAULT_ID = "";
    public static readonly DEFAULT_PROJECT_ID = "";
    public static readonly DEFAULT_RFICATEGORY_ID = "";
    public static readonly DEFAULT_CATEGORY_NAME = "";
    public static readonly DEFAULT_SUPPLIER_REFERENCE = "";
    public static readonly DEFAULT_CLIENT_REFERENCE = null;
    public static readonly DEFAULT_SUPPLIER_ID = "";
    public static readonly DEFAULT_RFI_STATUS_ID = "";
    public static readonly DEFAULT_PARENT_RFI_ID = null;
    public static readonly DEFAULT_REQUESTED_BY_USER_ID = null;
    public static readonly DEFAULT_REQUESTED_DATE = null;
    public static readonly DEFAULT_TITLE = "";
    public static readonly DEFAULT_DESCRIPTION = "";
    public static readonly DEFAULT_REQUESTEDTO = "";
    public static readonly DEFAULT_RESPONSE_REQUIRED_BY_DATE = null;
    public static readonly DEFAULT_LAST_UPDATED_DATE = null;
    public static readonly DEFAULT_CREATED_DATE = null;
    public static readonly DEFAULT_CREATED_BY_USER_ID = null;
    public static readonly DEFAULT_LAST_UPDATED_BY_USER_ID = null;
    public static readonly DEFAULT_IS_DELETED = false;
    public static readonly DEFAULT_ROW_VERSION = null;
    public static readonly DEFAULT_RFI_ATTACHMENTS = [];
    public static readonly DEFAULT_REVISION = null;
    public static readonly DEFAULT_RFI_DOCUMENTS = [];
    public static readonly DEFAULT_UNIQUE_ID = "";

    @observable
    public id: string | null = RFICreateFormModel.DEFAULT_ID;
    @observable
    public projectId: string = RFICreateFormModel.DEFAULT_PROJECT_ID;
    @observable
    public rfiCategoryId: string = RFICreateFormModel.DEFAULT_RFICATEGORY_ID;
    @observable
    public categoryName: string = RFICreateFormModel.DEFAULT_CATEGORY_NAME;
    @observable
    public supplierReference: string | null = RFICreateFormModel.DEFAULT_SUPPLIER_REFERENCE;
    @observable
    public clientReference: number | null = RFICreateFormModel.DEFAULT_CLIENT_REFERENCE;
    @observable
    public supplierId: string | null = RFICreateFormModel.DEFAULT_SUPPLIER_ID;
    @observable
    public rfiStatusId: string | null = RFICreateFormModel.DEFAULT_RFI_STATUS_ID;
    @observable
    public parentRFIId: string | null = RFICreateFormModel.DEFAULT_PARENT_RFI_ID;
    @observable
    public requestedByUserId: string | null = RFICreateFormModel.DEFAULT_REQUESTED_BY_USER_ID;
    @observable
    public requestedDate: string | null = RFICreateFormModel.DEFAULT_REQUESTED_DATE;
    @observable
    public title: string | null = RFICreateFormModel.DEFAULT_TITLE;
    @observable
    public description: string | null = RFICreateFormModel.DEFAULT_DESCRIPTION;
    @observable
    public requestedTo: string | null = RFICreateFormModel.DEFAULT_REQUESTEDTO;
    @observable
    public responseRequiredByDate: string | null = RFICreateFormModel.DEFAULT_RESPONSE_REQUIRED_BY_DATE;
    @observable
    public createdDate: string | null = RFICreateFormModel.DEFAULT_CREATED_DATE;
    @observable
    public createdByUserId: string | null = RFICreateFormModel.DEFAULT_CREATED_BY_USER_ID;
    @observable
    public lastUpdatedDate: string | null = RFICreateFormModel.DEFAULT_LAST_UPDATED_DATE;
    @observable
    public lastUpdatedByUserId: string | null = RFICreateFormModel.DEFAULT_LAST_UPDATED_BY_USER_ID;
    @observable
    public isDeleted: boolean = RFICreateFormModel.DEFAULT_IS_DELETED;
    @observable
    public rowVersion: string | null = RFICreateFormModel.DEFAULT_ROW_VERSION;
    @observable
    public rfiAttachments: RFIFormFileModel[] = RFICreateFormModel.DEFAULT_RFI_ATTACHMENTS;
    @observable
    public revision: number | null = RFICreateFormModel.DEFAULT_REVISION;
    @observable
    public rfiDocuments: RFIFormDocumentModel[] = RFICreateFormModel.DEFAULT_RFI_DOCUMENTS;
    @observable
    public uniqueId: string = RFICreateFormModel.DEFAULT_UNIQUE_ID;

    @action
    public reset = () => {
        this.id = RFICreateFormModel.DEFAULT_ID;
        this.projectId = RFICreateFormModel.DEFAULT_PROJECT_ID;
        this.rfiCategoryId = RFICreateFormModel.DEFAULT_RFICATEGORY_ID;
        this.categoryName = RFICreateFormModel.DEFAULT_CATEGORY_NAME;
        this.supplierReference = RFICreateFormModel.DEFAULT_SUPPLIER_REFERENCE;
        this.clientReference = RFICreateFormModel.DEFAULT_CLIENT_REFERENCE;
        this.supplierId = RFICreateFormModel.DEFAULT_SUPPLIER_ID;
        this.rfiStatusId = RFICreateFormModel.DEFAULT_RFI_STATUS_ID;
        this.parentRFIId = RFICreateFormModel.DEFAULT_PARENT_RFI_ID;
        this.requestedByUserId = RFICreateFormModel.DEFAULT_REQUESTED_BY_USER_ID;
        this.requestedDate = RFICreateFormModel.DEFAULT_REQUESTED_DATE;
        this.title = RFICreateFormModel.DEFAULT_TITLE;
        this.description = RFICreateFormModel.DEFAULT_DESCRIPTION;
        this.requestedTo = RFICreateFormModel.DEFAULT_REQUESTEDTO;
        this.responseRequiredByDate = RFICreateFormModel.DEFAULT_RESPONSE_REQUIRED_BY_DATE;
        this.createdDate = RFICreateFormModel.DEFAULT_CREATED_DATE;
        this.createdByUserId = RFICreateFormModel.DEFAULT_CREATED_BY_USER_ID;
        this.lastUpdatedDate = RFICreateFormModel.DEFAULT_LAST_UPDATED_DATE;
        this.lastUpdatedByUserId = RFICreateFormModel.DEFAULT_LAST_UPDATED_BY_USER_ID;
        this.isDeleted = RFICreateFormModel.DEFAULT_IS_DELETED;
        this.rowVersion = RFICreateFormModel.DEFAULT_ROW_VERSION;
        this.rfiAttachments.length = 0;
        this.revision = RFICreateFormModel.DEFAULT_REVISION;
        this.rfiDocuments.length = 0;
        this.uniqueId = RFICreateFormModel.DEFAULT_UNIQUE_ID;
    };

    @action
    public fromDto(dto: RFIAndRelatedResponseDTO): void {
        const rFIDto = dto.rfi;

        for (let key in rFIDto) {
            if (rFIDto.hasOwnProperty(key)) {
                if (key === "rfiAttachments") {
                    // do nothing
                } else if (this[key] instanceof Date) {
                    this[key] = new Date(rFIDto[key]);
                } else {
                    this[key] = rFIDto[key];
                }
            }
        }

        let processedFiles: RFIFormFileModel[] = [];

        for (const item of dto.rfiAttachments) {
            const itemToAdd = new RFIFormFileModel();
            itemToAdd.fromDto(item);
            processedFiles.push(itemToAdd);
        }

        this.rfiAttachments = [...this.rfiAttachments, ...processedFiles];

        let processedDocuments: RFIFormDocumentModel[] = [];

        for (const rfiDocument of dto.rfiDocuments) {
            const rfiDocumentToAdd = new RFIFormDocumentModel();
            rfiDocumentToAdd.fromDto(rfiDocument);
            processedDocuments.push(rfiDocumentToAdd);
        }

        this.rfiDocuments = [...this.rfiDocuments, ...processedDocuments];
    }

    public toDto() {
        const RFIFormModel: RFIDTO = {
            id: this.id,
            projectId: this.projectId,
            rfiCategoryId: this.rfiCategoryId,
            categoryName: this.categoryName,
            supplierReference: this.supplierReference,
            clientReference: this.clientReference,
            supplierId: this.supplierId,
            rfiStatusId: this.rfiStatusId,
            parentRFIId: this.parentRFIId,
            requestedByUserId: this.requestedByUserId,
            requestedDate: this.requestedDate,
            title: this.title,
            description: this.description,
            requestedTo: this.requestedTo,
            responseRequiredByDate: this.responseRequiredByDate,
            createdDate: this.createdDate,
            createdByUserId: this.createdByUserId,
            lastUpdatedDate: this.lastUpdatedDate,
            lastUpdatedByUserId: this.lastUpdatedByUserId,
            isDeleted: this.isDeleted,
            rowVersion: this.rowVersion,
            revision: this.revision,
            uniqueId: this.uniqueId,
        };

        return RFIFormModel;
    }

    @action
    public fromRelatedDto(dto: RFIRelatedResultResponseDTO): void {}

    @computed
    public get validateTitle(): string {
        return this.title === RFICreateFormModel.DEFAULT_TITLE ? "Please enter a Title" : "";
    }

    @computed
    public get validateSupplierReference(): string {
        return this.supplierReference === RFICreateFormModel.DEFAULT_SUPPLIER_REFERENCE ? "Please enter a Reference" : "";
    }

    @computed
    public get validateRequestedDate(): string {
        return this.requestedDate === RFICreateFormModel.DEFAULT_REQUESTED_DATE ? "Please select a date" : "";
    }

    @computed
    public get validateResponseRequiredByDate(): string {
        return this.responseRequiredByDate === RFICreateFormModel.DEFAULT_RESPONSE_REQUIRED_BY_DATE ? "Please select a date" : "";
    }

    @computed
    public get validateSupplierId(): string {
        return this.supplierId === RFICreateFormModel.DEFAULT_SUPPLIER_ID ? "Please select a Supplier" : "";
    }

    @computed
    public get validateStatusId(): string {
        return this.rfiStatusId === RFICreateFormModel.DEFAULT_RFI_STATUS_ID ? "Please select a Status" : "";
    }

    @computed
    public get validateDescription(): string {
        return this.description === RFICreateFormModel.DEFAULT_DESCRIPTION ? "Please enter a Description" : "";
    }
}

export interface RFIStatusDTO {
    id: string;
    displayName: string;
}

export interface RFISupplierDTO {
    id: string;
    displayName: string;
}

export interface RFICategoryDTO {
    id: string;
    displayName: string;
}

export interface RFIRequestedByDTO {
    id: string;
    displayName: string;
}

export interface RFIDarwinSupplierReferenceDTO {
    id: string;
    displayName: string;
}

export interface RFIDTO {
    id: string | null;
    projectId: string;
    rfiCategoryId: string;
    categoryName: string;
    supplierReference: string | null;
    clientReference: number | null;
    supplierId: string | null;
    rfiStatusId: string | null;
    parentRFIId: string | null;
    requestedByUserId: string | null;
    requestedDate: string | null;
    title: string | null;
    description: string | null;
    requestedTo: string | null;
    responseRequiredByDate: string | null;
    createdDate: string | null;
    createdByUserId: string | null;
    lastUpdatedDate: string | null;
    lastUpdatedByUserId: string | null;
    isDeleted: boolean;
    rowVersion: string | null;
    revision: number | null;
    uniqueId: string | null;
}

export interface UpsertRFIAndRelatedRequestDTO {
    rfi: RFIDTO;
    rfiAttachments: RFIFileDTO[];
    rfiDocuments: RFIDocumentDTO[];
}

export interface RFIAndRelatedResponseDTO extends RFIRelatedResultResponseDTO {
    rfi: RFIDTO;
    rfiAttachments: RFIFileDTO[];
    rfiDocuments: RFIDocumentDTO[];
}

export interface RFIProjectDetailsDTO {
    projectReference: string;
    projectName: string;
}

export interface RFIRelatedResultResponseDTO {
    suppliers: RFISupplierDTO[];
    statuses: RFIStatusDTO[];
    rfiProjectDetails: RFIProjectDetailsDTO;
    categories: RFICategoryDTO[];
}

export interface RFIAttachment {
    rfiId: string | null;
    fileName: string;
    fileURL: string;
    createdByUserId: string | null;
    isDeleted: boolean;
}

export interface RFICategoryDocument {
    rfiCategoryId: string | null;
    fileName: string;
    fileURL: string;
    createdByUserId: string | null;
    isDeleted: boolean;
}
